import React, {
  FormEvent,
  RefObject,
  useState,
  useContext,
  createContext,
  useEffect,
} from 'react';
import TextInput from 'react-autocomplete-input';
import {
  IAnnotations,
  IOutcome,
  ITextAnnotation,
  ITrainersData,
  ITrainersTool,
} from '@/types/trainers';
import { isValidOutcome } from '@/utils/trainers';
import { formattedLabel } from '@/utils/strings';
import { TrainerType } from '@/pages/trainers/[id]';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import TrainerButton from './TrainerButton';

interface ITrainerController {
  data?: ITrainersData;
  isDataReady: boolean;
  onSubmit: ({
    annotations,
    outcome,
    value,
  }: {
    annotations?: IAnnotations;
    outcome?: IOutcome;
    value?: string;
  }) => void;
  tool?: ITrainersTool;
  containerRef: RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

interface ITrainerControllerContext extends ITrainerController {
  containerRef: RefObject<HTMLDivElement>;
}

const TrainerControllerContext =
  createContext<ITrainerControllerContext | null>(null);

/**
 * Generates the controller buttons, select, and inputs for a trainer game
 * You can override any of the default buttons, select, or inputs depending on the game
 * You can change the order of elements
 * You can add additional elements per game
 * Any new type of control elements added on the DB should be added here
 *
 * Usage:
 * On the game component (e.g. DefaultTrainer.tsx):
 *
 * <TrainerController containerRef={controllerRef} data={data} ... }>
 *   <TrainerButtons />
 *   <TrainerSelect />
 *   <TrainerInputs />
 * </TrainerController>
 */

function TrainerController(props: ITrainerController) {
  const { containerRef, children, ...restProps } = props;

  const contextValue: ITrainerControllerContext = {
    containerRef,
    children,
    ...restProps,
  };
  return (
    <TrainerControllerContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className="relative flex w-full flex-wrap items-center justify-center border-y border-neutral-700 bg-neutral-800 py-2">
        {children}
      </div>
    </TrainerControllerContext.Provider>
  );
}

function TrainerButtons() {
  const context = useContext(TrainerControllerContext);

  if (!context) {
    throw new Error('TrainerButtons must be used within a TrainerController');
  }

  const { onSubmit, data, tool, isDataReady } = context;
  const buttons =
    data?.content?.buttonListOverride || tool?.args?.buttonList || [];
  const trainerType = tool?.widget;
  const prettyLabel = formattedLabel(data?.content?.label);

  const handleButtonClick = (buttonValue: string) => {
    let outcome: IOutcome | undefined;
    let annotations: ITextAnnotation[] | undefined;
    let value: string | undefined;
    if (isValidOutcome(buttonValue)) {
      outcome = buttonValue;
    } else {
      if (trainerType === TrainerType.TEXT_ANNOTATION) {
        annotations = [{ text: buttonValue.toString() }];
      } else {
        value = buttonValue;
      }
    }
    onSubmit({ annotations, outcome, value });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isDataReady || !buttons.length) return;
      // Find the button that matches the pressed hotkey
      const button = buttons.find((btn: any) => btn.hotkey === event.key);
      if (button) {
        handleButtonClick(button.value);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <>
      {buttons.map((button: any) => (
        <TrainerButton
          key={button.value}
          disabled={!isDataReady}
          styling={button}
          onClick={() => handleButtonClick(button.value)}
          label={button.label}
          sublabel={prettyLabel}
        />
      ))}
    </>
  );
}

function TrainerSelect() {
  const context = useContext(TrainerControllerContext);

  if (!context) {
    throw new Error('TrainerSelect must be used within a TrainerController');
  }

  const { onSubmit, data, tool, isDataReady } = context;
  const { leaseId } = data?.content || {};
  const trainerType = tool?.widget;
  const selectList =
    data?.content?.selectListOverride || tool?.args?.selectList || [];

  return (
    <>
      {selectList.length > 0 && (
        <div
          data-disabled={!isDataReady}
          className="m-1 flex h-12 min-h-12 cursor-pointer items-center justify-center rounded-md bg-neutral-100 p-1 px-2 text-center font-bold text-neutral-900 data-[disabled=true]:text-neutral-900 data-[disabled=true]:opacity-50">
          <Select
            key={leaseId}
            disabled={!isDataReady}
            onValueChange={newValue => {
              if (newValue === undefined) {
                return;
              }
              let outcome: IOutcome | undefined;
              let annotations: ITextAnnotation[] | undefined;
              let value: string | undefined;
              if (isValidOutcome(newValue)) {
                outcome = newValue;
              } else {
                if (trainerType === TrainerType.TEXT_ANNOTATION) {
                  annotations = [{ text: newValue.toString() }];
                } else {
                  value = newValue;
                }
              }
              onSubmit({ annotations, outcome, value });
            }}>
            <SelectTrigger>
              <SelectValue placeholder="Value" />
            </SelectTrigger>
            <SelectContent>
              {selectList.map((select: any) => (
                <SelectItem
                  key={select.label}
                  value={select.value}
                  className="cursor-pointer">
                  {select.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );
}

function TrainerInputs() {
  const context = useContext(TrainerControllerContext);

  if (!context) {
    throw new Error('TrainerInputs must be used within a TrainerController');
  }

  const { onSubmit, data, tool, isDataReady } = context;
  const inputList = tool?.args?.inputList || [];
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>(
    inputList.reduce(
      (acc: { [key: string]: string }, { name }: any) => ({
        ...acc,
        [name]: '',
      }),
      {},
    ),
  );
  const prettyLabel = formattedLabel(data?.content?.label);

  const resetInput = () => {
    setInputValues(
      inputList.reduce(
        (acc: { [key: string]: string }, { name }: any) => ({
          ...acc,
          [name]: '',
        }),
        {},
      ),
    );
  };

  const getHighlightedText = (text: string, wordsToHighlight: string[]) => {
    const escapedWords = wordsToHighlight.map(word =>
      word.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'),
    );
    const regex = new RegExp(`(${escapedWords.join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-azure-400">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const onInputSubmit = (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const annotations: ITextAnnotation[] = [];

    inputList.forEach(({ name, delimiter, replace }: any) => {
      const value = inputValues[name];
      if (!value) return;
      const values = value.split(delimiter);
      values.map((v: string) => {
        if (replace) {
          const [searchValue, newValue] = replace;
          const regex = new RegExp(searchValue, 'g');
          v = v.replace(regex, newValue);
        }
        annotations.push({ field: name, text: v.trim() });
      });
    });

    onSubmit({ annotations });
    resetInput();
  };

  const handleInputChange =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValues({ ...inputValues, [name]: event.target.value });
    };

  return (
    <>
      {inputList.length > 0 && (
        <form
          className="relative flex flex-wrap items-center justify-center"
          onSubmit={onInputSubmit}>
          {inputList.map((input: any) => (
            <div key={input.name} className="m-1 flex flex-col items-center">
              {input?.autoCompleteOptions?.length > 0 ? (
                <div
                  style={{ ...(input.width && { width: input.width }) }}
                  className="relative w-full max-w-[400px] bg-neutral-0 p-1 text-base">
                  <div className="pointer-events-none absolute left-0 top-0 z-10 w-full whitespace-pre-wrap break-words rounded p-1 text-base text-transparent">
                    {getHighlightedText(
                      inputValues[input.name],
                      input.autoCompleteOptions,
                    )}
                  </div>
                  <TextInput
                    key={input.name}
                    trigger={['', ' ']}
                    options={{
                      '': input.autoCompleteOptions,
                      ' ': input.autoCompleteOptions,
                    }}
                    value={inputValues[input.name]}
                    maxOptions={3}
                    minChars={2}
                    passThroughTab={false}
                    onChange={value => {
                      setInputValues({
                        ...inputValues,
                        [input.name]: value,
                      });
                    }}
                    placeholder="Describe the drive"
                    className="relative z-10 h-[80px] w-full rounded bg-transparent text-base text-neutral-900 outline-none"
                  />
                </div>
              ) : (
                <input
                  type={input.type || 'text'}
                  disabled={!isDataReady}
                  className={
                    'm-0.5 h-[48px] min-w-[250px] rounded border-neutral-0 bg-neutral-300 px-1 text-base text-neutral-900 transition-all duration-300 ease-in-out'
                  }
                  value={inputValues[input.name]}
                  style={{
                    opacity: !isDataReady ? 0.5 : 1,
                    ...(input.width && {
                      minWidth: input.width,
                      width: input.width,
                    }),
                  }}
                  onChange={handleInputChange(input.name)}
                  placeholder={input.placeholder}
                />
              )}
            </div>
          ))}
          <TrainerButton
            label="Submit"
            type="submit"
            onClick={onInputSubmit}
            disabled={!isDataReady}
            sublabel={prettyLabel}
          />
        </form>
      )}
    </>
  );
}

export {
  TrainerController as default,
  TrainerButtons,
  TrainerSelect,
  TrainerInputs,
};
